import { INTEGRATION_PROVIDER_LABELS, type RepositoryIntelligenceLink } from '@ashniva/types';
import { Button, Card, EmptyState, Table, type TableColumn } from '@ashniva/ui';
import { Link } from 'react-router';

import { CardsSkeleton } from '../../../shared/components/LoadingSkeletons';
import { useReauth } from '../../auth/reauth';
import { useRequestRegistration, useRiLinksQuery } from '../api';
import { classifyProblem } from '../ri-problems';
import { useSubmissionKey } from '../use-submission-key';
import { ProblemNotice } from './ProblemNotice';
import { RepositoryStatusCell } from './RepositoryStatusCell';

/**
 * Every repository linked to the organization's projects, with where each is with Repository Intelligence (P14-02).
 * A repository nobody asked to index can be requested from here; everything else happens on its own page.
 */
export function OrganizationRepositories() {
  const links = useRiLinksQuery();
  const columns: TableColumn<RepositoryIntelligenceLink>[] = [
    {
      key: 'repository',
      header: 'Repository',
      render: (link) => (
        <Link
          to={`/projects/${encodeURIComponent(link.project.id)}/code/${encodeURIComponent(link.id)}`}
        >
          {link.owner}/{link.name}
        </Link>
      ),
    },
    { key: 'project', header: 'Project', hideOnMobile: true, render: (link) => link.project.code },
    {
      key: 'host',
      header: 'Host',
      hideOnMobile: true,
      render: (link) => INTEGRATION_PROVIDER_LABELS[link.provider],
    },
    { key: 'state', header: 'State', render: (link) => <RepositoryStatusCell linkId={link.id} /> },
    {
      key: 'action',
      header: <span className="sr-only">Actions</span>,
      render: (link) => (link.registrationRequested ? null : <RequestIndexing link={link} />),
    },
  ];
  return (
    <Card title="Repositories" subtitle="Every repository linked to a project of the organization.">
      {links.isLoading ? <CardsSkeleton label="repositories" cards={1} /> : null}
      {links.isError ? (
        <ProblemNotice error={links.error} onRetry={() => void links.refetch()} />
      ) : null}
      {links.isSuccess ? (
        <Table
          aria-label="Repositories"
          columns={columns}
          rows={links.data.items}
          rowKey={(link) => link.id}
          empty={
            <EmptyState
              size="sm"
              icon="○"
              title="No repositories are linked yet"
              description="Repositories linked to projects appear here."
            />
          }
        />
      ) : null}
    </Card>
  );
}

function RequestIndexing({ link }: { link: RepositoryIntelligenceLink }) {
  const reauth = useReauth();
  const request = useRequestRegistration(link.id);
  const keys = useSubmissionKey();
  const onClick = async () => {
    if (request.isPending || reauth.active) return;
    const token = await reauth.request().catch(() => null);
    if (!token) return;
    const clientRequestId = keys.start('register');
    if (!clientRequestId) return;
    request.mutate({ headers: reauth.headers(token), clientRequestId }, keys.callbacks());
  };
  if (request.isSuccess || (request.isError && classifyProblem(request.error) === 'conflict')) {
    return <span className="ri-muted">Requested</span>;
  }
  return (
    <>
      <Button
        size="sm"
        loading={request.isPending}
        disabled={request.isPending}
        aria-label={`Request indexing of ${link.owner}/${link.name}`}
        onClick={() => void onClick()}
      >
        Request indexing
      </Button>
      {request.isError ? <ProblemNotice error={request.error} size="sm" /> : null}
      {reauth.modal}
    </>
  );
}
